import {
  all,
  takeLatest,
  takeEvery,
  put,
  call,
  fork,
  select,
  take,
} from 'redux-saga/effects';
import {
  getDatabase,
  ref,
  update,
  get,
  push,
  remove,
  onValue,
} from 'firebase/database';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDocFromServer,
} from 'firebase/firestore';
import moment from 'moment-timezone';
import axios from 'axios';
import { eventChannel } from 'redux-saga';
import actions from './actions';
import appActions from '../app/actions';
import authActions from '../auth/actions';
import notification from '../../components/notification';

const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;

const getMainUserFromStore = (state) => state.Auth.mainUser;
const getSelectedAddressFromStore = (state) => state.App.selectedAddress;
const getAiPermissionsFromStore = (state) => state.AI.permissions;

function createPatientsListener(mainUser, selectedAddress) {
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    if (!currentUser) {
      throw new Error('Usuário não autenticado');
    }
    ({ uid } = currentUser);
  }

  const db = getDatabase();
  const patientsRef = ref(db, `users/${uid}/ai/${selectedAddress}/patientsNeedingAttention`);

  const listener = eventChannel((emit) => {
    const unsubscribe = onValue(patientsRef, (snapshot) => {
      const data = snapshot.val();
      const patients = data ? Object.keys(data).map((key) => ({ id: key, ...data[key] })) : [];
      emit(patients);
    }, (error) => {
      emit({ error: error.message });
    });

    return () => unsubscribe();
  });

  return listener;
}

function removeOldPatientsOnDB(items, selectedAddress, mainUser) {
  const db = getDatabase();
  const dbRef = ref(db);
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }
  const updates = {};
  items.forEach((el) => {
    updates[`users/${uid}/ai/${selectedAddress}/patientsNeedingAttention/${el.id}`] = null;
  });
  // return true;
  return update(dbRef, updates);
}

export function* watchPatientsNeedingAttention() {
  let currentListener = null;
  let previousPatients = [];
  yield takeLatest([
    actions.FETCH_PATIENTS_NEEDING_ATTENTION,
  ], function* () {
    try {
      let aiPermissions = yield select(getAiPermissionsFromStore);
      if (aiPermissions === null || aiPermissions === undefined) {
        yield put({ type: actions.GET_AI_PERMISSION });
        aiPermissions = yield select(getAiPermissionsFromStore);
      }
      if (!aiPermissions || !aiPermissions.enabled) {
        return;
      }
      if (currentListener) {
        currentListener.close();
        currentListener = null;
      }
      const mainUser = yield select(getMainUserFromStore);
      const selectedAddress = yield select(getSelectedAddressFromStore);
      currentListener = yield call(createPatientsListener, mainUser, selectedAddress);
      yield takeEvery(currentListener, function* (patients) {
        if (patients.error) {
          yield put({ type: actions.FETCH_PATIENTS_NEEDING_ATTENTION_ERROR, payload: patients.error });
        } else {
          const itemsToKeep = [];
          const itemsToDelete = [];
          if (patients?.length > 0) {
            const limit = moment().subtract(2, 'weeks');
            patients.forEach((el) => {
              if (moment(el.createdAt).isSameOrAfter(limit)) {
                itemsToKeep.push(el);
              } else {
                itemsToDelete.push(el);
              }
            });
          }
          if (itemsToDelete.length > 0) {
            yield call(
              removeOldPatientsOnDB,
              itemsToDelete,
              selectedAddress,
              mainUser,
            );
          }
          const totalWaitingWhatsApp = itemsToKeep.filter((patient) => !patient.responded).length;
          const previousPatientIds = previousPatients.map((p) => p.id);
          const newPatients = itemsToKeep.filter((p) => !previousPatientIds.includes(p.id));
          if (newPatients.length > 0 && previousPatients.length > 0) {
            const patientNames = newPatients.map((p) => p.name || 'Paciente sem nome').join(', ');
            const message = newPatients.length === 1
              ? 'Novo paciente precisa de atenção'
              : `${newPatients.length} novos pacientes precisam de atenção`;

            notification('info', message, `${patientNames}`, 8);
          }
          previousPatients = [...itemsToKeep];
          yield put({
            type: actions.FETCH_PATIENTS_NEEDING_ATTENTION_SUCCESS,
            payload: {
              patients: itemsToKeep,
              totalWaitingWhatsApp,
            },
          });
        }
      });
      yield take(appActions.CANCEL_LISTENERS);
      currentListener.close();
      currentListener = null;
      yield all([
        put({ type: appActions.CANCEL_LISTENERS_SUCCESS }),
        put({ type: actions.RESET_PATIENTS_NEEDING_ATTENTION_LISTENER }),
      ]);
    } catch (error) {
      console.warn(error.message);
      yield put({ type: actions.FETCH_PATIENTS_NEEDING_ATTENTION_ERROR, payload: error.message || 'Erro ao conectar com Firebase' });
    }
  });
}

function addPatientToDB(patient, mainUser, selectedAddress) {
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }

  const db = getDatabase();
  const patientsRef = ref(db, `users/${uid}/ai/${selectedAddress}/patientsNeedingAttention`);
  const newPatientRef = push(patientsRef);

  const patientData = {
    ...patient,
    name: patient.name || 'Sem Nome',
    createdAt: new Date().toISOString(),
    responded: false,
  };

  return update(ref(db), {
    [`users/${uid}/ai/${selectedAddress}/patientsNeedingAttention/${newPatientRef.key}`]: patientData,
  });
}

function* addPatientNeedingAttention() {
  yield takeLatest(actions.ADD_PATIENT_NEEDING_ATTENTION, function* (action) {
    try {
      // Check if user has AI permissions
      const aiPermissions = yield select(getAiPermissionsFromStore);
      if (!aiPermissions || !aiPermissions.enabled) {
        console.warn('AI permissions not available, cannot add patient');
        yield put({ type: actions.ADD_PATIENT_NEEDING_ATTENTION_ERROR, payload: 'AI permissions not available' });
        return;
      }

      const mainUser = yield select(getMainUserFromStore);
      const selectedAddress = yield select(getSelectedAddressFromStore);
      yield call(addPatientToDB, action.payload, mainUser, selectedAddress);
      yield put({ type: actions.ADD_PATIENT_NEEDING_ATTENTION_SUCCESS, payload: action.payload });
    } catch (error) {
      console.warn(error.message);
      yield put({ type: actions.ADD_PATIENT_NEEDING_ATTENTION_ERROR, payload: error.message || 'Erro ao adicionar paciente' });
    }
  });
}

function updatePatientStatusInDB(patientId, status, mainUser, selectedAddress) {
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }

  const db = getDatabase();
  const respondedStatus = status !== undefined ? status : true;

  const updates = {};
  updates[`users/${uid}/ai/${selectedAddress}/patientsNeedingAttention/${patientId}/responded`] = respondedStatus;
  updates[`users/${uid}/ai/${selectedAddress}/patientsNeedingAttention/${patientId}/updatedAt`] = new Date().toISOString();

  return update(ref(db), updates);
}

function* markPatientAsAttended() {
  yield takeLatest(actions.MARK_PATIENT_AS_ATTENDED, function* (action) {
    try {
      // Check if user has AI permissions
      const aiPermissions = yield select(getAiPermissionsFromStore);
      if (!aiPermissions || !aiPermissions.enabled) {
        console.warn('AI permissions not available, cannot mark patient as attended');
        yield put({ type: actions.MARK_PATIENT_AS_ATTENDED_ERROR, payload: 'AI permissions not available' });
        return;
      }

      const { patientId, status } = action.payload;
      const mainUser = yield select(getMainUserFromStore);
      const selectedAddress = yield select(getSelectedAddressFromStore);
      yield call(updatePatientStatusInDB, patientId, status, mainUser, selectedAddress);
      // Get current patients array from state
      const currentState = yield select((state) => state.AI);
      const updatedPatientsNeedingAttention = currentState.patientsNeedingAttention.map((patient) => {
        if (patient.id === patientId) {
          return { ...patient, responded: status, updatedAt: new Date().toISOString() };
        }
        return patient;
      });
      yield put({
        type: actions.MARK_PATIENT_AS_ATTENDED_SUCCESS,
        payload: {
          patientId,
          status,
          patientsNeedingAttention: updatedPatientsNeedingAttention,
        },
      });
    } catch (error) {
      console.warn(error.message);
      yield put({ type: actions.MARK_PATIENT_AS_ATTENDED_ERROR, payload: error.message || 'Erro ao marcar paciente como atendido' });
    }
  });
}

function removePatientFromDB(patientId, mainUser, selectedAddress) {
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }

  const db = getDatabase();
  return remove(ref(db, `users/${uid}/ai/${selectedAddress}/patientsNeedingAttention/${patientId}`));
}

function* removePatient() {
  yield takeLatest(actions.REMOVE_PATIENT, function* (action) {
    try {
      // Check if user has AI permissions
      const aiPermissions = yield select(getAiPermissionsFromStore);
      if (!aiPermissions || !aiPermissions.enabled) {
        console.warn('AI permissions not available, cannot remove patient');
        yield put({ type: actions.REMOVE_PATIENT_ERROR, payload: 'AI permissions not available' });
        return;
      }

      const patientId = action.payload;
      const mainUser = yield select(getMainUserFromStore);
      const selectedAddress = yield select(getSelectedAddressFromStore);
      yield call(removePatientFromDB, patientId, mainUser, selectedAddress);
      // Get current patients array from state
      const currentState = yield select((state) => state.AI);
      const updatedPatientsNeedingAttention = currentState.patientsNeedingAttention.filter(
        (patient) => patient.id !== patientId,
      );
      yield put({
        type: actions.REMOVE_PATIENT_SUCCESS,
        payload: {
          patientId,
          patientsNeedingAttention: updatedPatientsNeedingAttention,
        },
      });
    } catch (error) {
      console.warn(error.message);
      yield put({ type: actions.REMOVE_PATIENT_ERROR, payload: error.message || 'Erro ao remover paciente' });
    }
  });
}

function* updatePatientStatus() {
  yield takeLatest(actions.UPDATE_PATIENT_STATUS, function* (action) {
    try {
      // Check if user has AI permissions
      const aiPermissions = yield select(getAiPermissionsFromStore);
      if (!aiPermissions || !aiPermissions.enabled) {
        console.warn('AI permissions not available, cannot update patient status');
        yield put({ type: actions.UPDATE_PATIENT_STATUS_ERROR, payload: 'AI permissions not available' });
        return;
      }

      const { patientId, status } = action.payload;
      const mainUser = yield select(getMainUserFromStore);
      const selectedAddress = yield select(getSelectedAddressFromStore);
      yield call(updatePatientStatusInDB, patientId, status, mainUser, selectedAddress);
      // Get current patients array from state
      const currentState = yield select((state) => state.AI);
      const updatedPatientsNeedingAttention = currentState.patientsNeedingAttention.map((patient) => (
        patient.id === patientId
          ? { ...patient, responded: status, updatedAt: new Date().toISOString() }
          : patient
      ));
      yield put({
        type: actions.UPDATE_PATIENT_STATUS_SUCCESS,
        payload: {
          patientId,
          responded: status,
          patientsNeedingAttention: updatedPatientsNeedingAttention,
        },
      });
    } catch (error) {
      console.warn(error.message);
      yield put({ type: actions.UPDATE_PATIENT_STATUS_ERROR, payload: error.message || 'Erro ao atualizar status do paciente' });
    }
  });
}

function getWhatsAppMessageFromDB(mainUser, selectedAddress) {
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }

  const db = getDatabase();
  const messageRef = ref(db, `users/${uid}/ai/${selectedAddress}/aiSettings/whatsappMessage`);
  return get(messageRef);
}

function* fetchWhatsAppMessage() {
  yield takeLatest(actions.FETCH_WHATSAPP_MESSAGE, function* () {
    try {
      // Check if user has AI permissions
      const aiPermissions = yield select(getAiPermissionsFromStore);
      if (!aiPermissions || !aiPermissions.enabled) {
        console.warn('AI permissions not available, cannot fetch WhatsApp message');
        yield put({ type: actions.FETCH_WHATSAPP_MESSAGE_ERROR, payload: 'AI permissions not available' });
        return;
      }

      const mainUser = yield select(getMainUserFromStore);
      const selectedAddress = yield select(getSelectedAddressFromStore);
      const snapshot = yield call(getWhatsAppMessageFromDB, mainUser, selectedAddress);

      let whatsappMessage = 'Olá vou dar continuidade com seu atendimento!';
      if (snapshot.exists()) {
        whatsappMessage = snapshot.val();
      }

      yield put({ type: actions.FETCH_WHATSAPP_MESSAGE_SUCCESS, payload: whatsappMessage });
    } catch (error) {
      console.warn(error.message);
      yield put({ type: actions.FETCH_WHATSAPP_MESSAGE_ERROR, payload: error.message || 'Erro ao buscar mensagem do WhatsApp' });
    }
  });
}

function updateWhatsAppMessageInDB(whatsappMessage, mainUser, selectedAddress) {
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }

  const db = getDatabase();
  const updates = {};
  updates[`users/${uid}/ai/${selectedAddress}/aiSettings/whatsappMessage`] = whatsappMessage;

  return update(ref(db), updates);
}

function getAiProfessionalPermissionsFromDB(mainUser) {
  const fs = getFirestore();
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }
  const userRef = doc(
    fs,
    'professionals',
    uid,
    'ai',
    'users',
  );
  return getDocFromServer(userRef);
}

function* updateWhatsAppMessage() {
  yield takeLatest(actions.UPDATE_WHATSAPP_MESSAGE, function* (action) {
    try {
      // Check if user has AI permissions
      const aiPermissions = yield select(getAiPermissionsFromStore);
      if (!aiPermissions || !aiPermissions.enabled) {
        console.warn('AI permissions not available, cannot update WhatsApp message');
        yield put({ type: actions.UPDATE_WHATSAPP_MESSAGE_ERROR, payload: 'AI permissions not available' });
        return;
      }

      const whatsappMessage = action.payload;
      const mainUser = yield select(getMainUserFromStore);
      const selectedAddress = yield select(getSelectedAddressFromStore);
      yield call(updateWhatsAppMessageInDB, whatsappMessage, mainUser, selectedAddress);
      yield put({ type: actions.UPDATE_WHATSAPP_MESSAGE_SUCCESS, payload: whatsappMessage });
    } catch (error) {
      console.warn(error.message);
      yield put({ type: actions.UPDATE_WHATSAPP_MESSAGE_ERROR, payload: error.message || 'Erro ao atualizar mensagem do WhatsApp' });
    }
  });
}

export function* getAiProfessionalPermissions() {
  yield takeEvery([
    authActions.LOGIN_SUCCESS,
    actions.GET_AI_PERMISSION,
  ], function* () {
    try {
      const mainUser = yield select(getMainUserFromStore);
      const aiPermissions = yield call(getAiProfessionalPermissionsFromDB, mainUser);
      if (aiPermissions.data()) {
        const permissionsData = aiPermissions.data();
        const hasAnyPermission = Object.values(permissionsData).some((permission) => permission === true);
        yield put({
          type: actions.GET_AI_PERMISSION_SUCCESS,
          payload: {
            permissions: {
              ...permissionsData,
              enabled: hasAnyPermission,
            },
          },
        });
      } else {
        yield put({
          type: actions.GET_AI_PERMISSION_SUCCESS,
          payload: {
            permissions: {
              enabled: false,
            },
          },
        });
      }
    } catch (error) {
      console.warn(error.message);
    }
  });
}

// --------------- Painel da IA (mensagens enviadas) ---------------

// Cloud function getAiStats: totais dos contadores de uso (systemUsage) da conta —
// mensagens do atendente (ai_message) e automáticas (whatsapp_message).
function getAiStatsFromApi(idToken) {
  return axios.post(
    `${ROOT_URL}/getAiStats`,
    {},
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
}

function* fetchAiDashboard() {
  yield takeLatest(actions.FETCH_AI_DASHBOARD, function* () {
    try {
      const aiPermissions = yield select(getAiPermissionsFromStore);
      if (!aiPermissions || !aiPermissions.enabled) return;
      const { currentUser } = getAuth();
      const idToken = yield call(() => currentUser.getIdToken());
      const response = yield call(getAiStatsFromApi, idToken);
      yield put({
        type: actions.FETCH_AI_DASHBOARD_SUCCESS,
        payload: { messages: (response.data && response.data.features) || null },
      });
    } catch (error) {
      console.warn('[ai] message stats error', error);
      yield put({ type: actions.FETCH_AI_DASHBOARD_ERROR, payload: error.message || 'Erro ao buscar mensagens enviadas' });
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(watchPatientsNeedingAttention),
    fork(addPatientNeedingAttention),
    fork(markPatientAsAttended),
    fork(removePatient),
    fork(updatePatientStatus),
    fork(fetchWhatsAppMessage),
    fork(updateWhatsAppMessage),
    fork(getAiProfessionalPermissions),
    fork(fetchAiDashboard),
  ]);
}
